import React, { useEffect, useState, useCallback } from "react";
import api, { fmtMoney, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

function StatusBadge({ s }) {
  const map = { success: "bg-emerald-500/10 text-emerald-600", pending: "bg-amber-500/10 text-amber-600", rejected: "bg-rose-500/10 text-rose-600" };
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium capitalize ${map[s]}`}>{s}</span>;
}

export default function AdminPayouts() {
  const [filter, setFilter] = useState("pending");
  const [rows, setRows] = useState([]);

  const load = useCallback(() => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    api.get(`/admin/payouts${q}`).then(({ data }) => setRows(data)).catch(() => {});
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="admin-payouts-page">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Payout requests</h1>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList data-testid="payout-filter-tabs">
          <TabsTrigger value="pending" data-testid="filter-pending">Pending</TabsTrigger>
          <TabsTrigger value="success" data-testid="filter-success">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="filter-rejected">Rejected</TabsTrigger>
          <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="space-y-3">
        {rows.length === 0 && <div className="py-12 text-center text-slate-400 text-sm">No payout requests</div>}
        {rows.map((t) => (
          <div key={t.id} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 sm:p-5" data-testid={`payout-row-${t.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono-data text-lg font-bold text-slate-900 dark:text-white">{fmtMoney(t.amount)}</span>
                  <StatusBadge s={t.status} />
                </div>
                <div className="text-sm text-slate-500 mt-1">{t.user?.name} • {t.user?.email}</div>
                <div className="text-xs text-slate-500 mt-2 font-mono-data">
                  {t.account_name} • A/C {t.account_number} • {t.ifsc} {t.bank_name ? `• ${t.bank_name}` : ""}
                </div>
                {t.utr && <div className="text-xs text-emerald-600 mt-1 font-mono-data">UTR {t.utr}</div>}
                {t.reason && <div className="text-xs text-rose-600 mt-1">Reason: {t.reason}</div>}
                <div className="text-[11px] text-slate-400 mt-1">{new Date(t.created_at).toLocaleString("en-IN")}</div>
              </div>
              {t.status === "pending" && (
                <div className="flex gap-2">
                  <ApproveDialog t={t} onDone={load} />
                  <RejectDialog t={t} onDone={load} />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ApproveDialog({ t, onDone }) {
  const [open, setOpen] = useState(false);
  const [utr, setUtr] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{12}$/.test(utr)) return toast.error("UTR must be exactly 12 digits");
    setLoading(true);
    try {
      await api.post(`/admin/payouts/${t.id}/approve`, { utr });
      toast.success("Payout approved");
      setOpen(false); onDone();
    } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" className="rounded-xl bg-emerald-600 hover:bg-emerald-700" data-testid={`approve-btn-${t.id}`}><Check className="h-4 w-4 mr-1" /> Approve</Button></DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-heading">Approve payout • {fmtMoney(t.amount)}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5"><Label>Bank UTR (12 digits)</Label><Input value={utr} onChange={(e) => setUtr(e.target.value.replace(/\D/g, "").slice(0, 12))} className="font-mono-data" placeholder="123456789012" required data-testid="approve-utr-input" /></div>
          <DialogFooter><Button type="submit" disabled={loading} className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700" data-testid="approve-submit-btn">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm approval"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RejectDialog({ t, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/admin/payouts/${t.id}/reject`, { reason });
      toast.success("Payout rejected & refunded");
      setOpen(false); onDone();
    } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="destructive" className="rounded-xl" data-testid={`reject-btn-${t.id}`}><X className="h-4 w-4 mr-1" /> Reject</Button></DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-heading">Reject payout • {fmtMoney(t.amount)}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <p className="text-sm text-slate-500">The amount will be refunded to the user's payout wallet.</p>
          <div className="space-y-1.5"><Label>Reason (optional)</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-reason-input" /></div>
          <DialogFooter><Button type="submit" disabled={loading} variant="destructive" className="w-full rounded-xl" data-testid="reject-submit-btn">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm rejection"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
